import { Controller } from "@hotwired/stimulus"

// Filter panel for the capacity-planning board (trimmed plantafel-filter): open/close, category
// switch, search, "Alle"/individual checkbox logic, reset. Which categories exist depends on the
// layout — the employee layout adds Teams and Mitarbeiter — so every category is resolved from
// the targets actually rendered.
export default class extends Controller {
  static targets = [
    "panel",
    "form",
    "searchInput",
    "categoryButton",
    "categoryContent",
    "filterItem",
    "allSitesCheckbox",
    "siteCheckbox",
    "maxMessage",
    "allTasksCheckbox",
    "taskCheckbox",
    "allTradesCheckbox",
    "tradeCheckbox",
    "allTeamsCheckbox",
    "teamCheckbox",
    "allEmployeesCheckbox",
    "employeeCheckbox",
    "employeesMaxMessage",
    "siteFilterRadio",
    "plannedSiteField",
  ]

  static values = { maxSites: Number, maxEmployees: Number }

  connect() {
    // The first OFFERED category: the Auslastung keeps the Baustellen/Aufgaben buttons in the
    // form but hidden, and opens on Gewerke.
    const firstOffered =
      this.categoryButtonTargets.find((button) => !button.classList.contains("hidden")) ||
      this.categoryButtonTargets[0]
    this.currentCategory = firstOffered ? firstOffered.dataset.category : "sites"
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
  }

  toggle(event) {
    event.stopPropagation()
    this.panelTarget.classList.toggle("hidden")
    if (!this.panelTarget.classList.contains("hidden")) {
      this.searchInputTarget.value = ""
      this.showAllItems()
    }
  }

  close() {
    this.panelTarget.classList.add("hidden")
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  selectCategory(event) {
    this.currentCategory = event.currentTarget.dataset.category

    this.categoryButtonTargets.forEach((button) => {
      const active = button.dataset.category === this.currentCategory
      button.classList.toggle("bg-gray-100", active)
      button.classList.toggle("font-medium", active)
      button.classList.toggle("text-gray-900", active)
      button.classList.toggle("text-gray-600", !active)
    })

    this.categoryContentTargets.forEach((content) => {
      content.classList.toggle("hidden", content.dataset.category !== this.currentCategory)
    })

    this.searchInputTarget.value = ""
    this.showAllItems()
  }

  search(event) {
    const query = event.target.value.toLowerCase().trim()
    const content = this.categoryContentTargets.find(
      (c) => c.dataset.category === this.currentCategory,
    )
    if (!content) return

    content.querySelectorAll('[data-planning-filter-target="filterItem"]').forEach((item) => {
      const text = item.dataset.searchText || ""
      item.classList.toggle("hidden", query !== "" && !text.includes(query))
    })
  }

  showAllItems() {
    this.filterItemTargets.forEach((item) => item.classList.remove("hidden"))
  }

  // The rendered filter categories, each with its item checkboxes and the "Alle" master. A layout
  // that omits a category simply contributes no entry.
  categories() {
    return [
      { items: this.siteCheckboxTargets, all: this.safeTarget("allSitesCheckbox") },
      { items: this.taskCheckboxTargets, all: this.safeTarget("allTasksCheckbox") },
      { items: this.tradeCheckboxTargets, all: this.safeTarget("allTradesCheckbox") },
      { items: this.teamCheckboxTargets, all: this.safeTarget("allTeamsCheckbox") },
      { items: this.employeeCheckboxTargets, all: this.safeTarget("allEmployeesCheckbox") },
    ].filter((category) => category.all)
  }

  safeTarget(name) {
    const capitalized = name.charAt(0).toUpperCase() + name.slice(1)
    return this[`has${capitalized}Target`] ? this[`${name}Target`] : null
  }

  // Checking "Alle" clears the individual selections in that category.
  toggleAll(event) {
    if (!event.target.checked) return
    const category = this.categories().find((c) => c.all === event.target)
    category?.items.forEach((cb) => (cb.checked = false))
  }

  // Checking an individual item clears "Alle"; clearing the last re-checks it.
  updateSelection(event) {
    const category = this.categories().find((c) => c.items.includes(event.target))
    if (!category?.all) return

    // Two categories are capped: sites because the board renders one row each, employees because
    // the selection is persisted in the cookie-backed session.
    if (event.target.checked && this.exceedsLimit(category)) {
      event.target.checked = false
      this.flashMaxMessage(this.limitFor(category)?.message)
      return
    }

    category.all.checked = !category.items.some((cb) => cb.checked)
  }

  // The cap for a category, or undefined when it is uncapped.
  limitFor(category) {
    if (category.all === this.safeTarget("allSitesCheckbox")) {
      return { max: this.maxSitesValue, message: this.safeTarget("maxMessage") }
    }
    if (category.all === this.safeTarget("allEmployeesCheckbox")) {
      return { max: this.maxEmployeesValue, message: this.safeTarget("employeesMaxMessage") }
    }
    return undefined
  }

  exceedsLimit(category) {
    const limit = this.limitFor(category)
    if (!limit || !(limit.max > 0)) return false
    return category.items.filter((cb) => cb.checked).length > limit.max
  }

  flashMaxMessage(element) {
    if (!element) return
    element.classList.remove("hidden")
    clearTimeout(this.maxMessageTimeout)
    this.maxMessageTimeout = setTimeout(() => {
      element.classList.add("hidden")
    }, 3000)
  }

  reset() {
    this.categories().forEach((category) => {
      category.items.forEach((cb) => (cb.checked = false))
      if (category.all) category.all.checked = true
    })
    this.siteFilterRadioTargets.forEach((radio) => (radio.checked = radio.value === "all"))
    // The sidebar drill-down is carried by hidden fields, not checkboxes, so it needs clearing
    // explicitly — "Zurücksetzen" means every filter, not just the ones the panel draws. Blanked
    // rather than removed: sanitize_filter_ids drops the empty value server-side.
    this.plannedSiteFieldTargets.forEach((field) => (field.value = ""))
    if (this.hasFormTarget) this.formTarget.requestSubmit()
  }
}
